import { Check, ChevronLeft, ListChecks, Search, SkipForward } from "@/components/ui/icons";
import { useQueries } from "@tanstack/react-query";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { PublicShowSkeleton } from "@/components/shows/PublicShowSkeleton";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Screen } from "@/components/ui/Screen";
import { usePublicShow } from "@/hooks/usePublicShow";
import {
  fetchPublicShowCard,
  fetchPublicShowInventory,
  isMissingShowListing,
} from "@/hooks/usePublicShowInventory";
import { DESKTOP_BREAKPOINT } from "@/hooks/useResponsive";
import { type ShowHuntStatus, useShowHuntList } from "@/hooks/useShowHuntList";
import { useShowHuntSnapshot } from "@/hooks/useShowHuntSnapshot";
import { formatCardIdentity } from "@/lib/catalog/cardIdentity";
import {
  availabilityLabel,
  type ShowCatalogCard,
  treatmentLabel,
} from "@/lib/shows/showCardFinding";
import { MAX_HUNT_CARDS, parseHuntQueries, resolveShowHuntRoute } from "@/lib/shows/showHunt";
import { spaceActivates } from "@/lib/ui/webKeyboard";
import { useThemeColors } from "@/theme/theme";

type Stage = "edit" | "resolve" | "route";

const one = (value: string | string[] | undefined) => Array.isArray(value) ? value[0] : value;
const tableLabel = (numbers: readonly string[]) =>
  numbers.length === 1
    ? `Table ${numbers[0]}`
    : numbers.length > 1
    ? `Tables ${numbers.join(", ")}`
    : "Table not assigned";
const savedAtFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function Candidate({ card, selected, onPress }: {
  card: ShowCatalogCard;
  selected: boolean;
  onPress: () => void;
}) {
  const c = useThemeColors();
  const identity = formatCardIdentity(card.set_name, card.collector_number, treatmentLabel(card));
  return (
    <Pressable
      testID="hunt-candidate"
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      aria-checked={selected}
      accessibilityLabel={`${card.name}, ${identity ?? "card"}, ${
        availabilityLabel(card.vendor_count, card.quantity)
      }`}
      onPress={onPress}
      {...spaceActivates(onPress)}
      className={selected
        ? "min-h-20 flex-row items-center gap-3 rounded-xl border border-primary bg-accent-soft p-3"
        : "min-h-20 flex-row items-center gap-3 rounded-xl border border-border bg-surface p-3 active:bg-surface-muted"}
    >
      {card.image_url
        ? (
          <Image
            source={{ uri: card.image_url }}
            className="h-14 w-10 rounded-md bg-surface-muted"
            resizeMode="cover"
            accessible={false}
            accessibilityIgnoresInvertColors
          />
        )
        : (
          <View className="h-14 w-10 items-center justify-center rounded-md bg-surface-muted">
            <Text className="text-[10px] font-label uppercase text-muted-foreground">
              {(card.game ?? "TCG").slice(0, 3)}
            </Text>
          </View>
        )}
      <View className="min-w-0 flex-1 gap-1">
        <Text className="font-title text-foreground" numberOfLines={2}>{card.name}</Text>
        {identity
          ? <Text className="text-xs font-body text-muted-foreground">{identity}</Text>
          : null}
        <Text className="text-xs font-body text-primary">
          {availabilityLabel(card.vendor_count, card.quantity)}
        </Text>
      </View>
      <View
        aria-hidden
        className={selected
          ? "h-7 w-7 items-center justify-center rounded-full bg-primary"
          : "h-7 w-7 rounded-full border border-border"}
      >
        {selected ? <Check size={16} color={c.primaryForeground} /> : null}
      </View>
    </Pressable>
  );
}

function statusLabel(status: ShowHuntStatus) {
  if (status === "found") return "Found";
  if (status === "skipped") return "Skipped";
  return "Still hunting";
}

export default function ShowHuntScreen() {
  const showId = one(useLocalSearchParams<{ showId: string; }>().showId);
  const insets = useSafeAreaInsets();
  const colors = useThemeColors();
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const showQuery = usePublicShow(showId);
  const huntList = useShowHuntList(showId);
  const {
    snapshot: savedRoute,
    hydrated: snapshotHydrated,
    save: saveRouteSnapshot,
  } = useShowHuntSnapshot(showId);
  const [stage, setStage] = useState<Stage>(huntList.hunt?.cards.length ? "route" : "edit");
  const [draft, setDraft] = useState(() => huntList.hunt?.inputLines.join("\n") ?? "");
  const [queries, setQueries] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, string>>({});

  const restored = useRef(false);
  // Storage can load after the first render, so restore a saved hunt once when it arrives. Only
  // once: keying off an empty draft would restore the route whenever the textarea is cleared.
  useEffect(() => {
    if (restored.current || !huntList.hydrated) return;
    restored.current = true;
    if (draft.trim() || !huntList.hunt?.cards.length) return;
    setDraft(huntList.hunt.inputLines.join("\n"));
    setStage("route");
  }, [draft, huntList.hydrated, huntList.hunt]);

  const searches = useQueries({
    queries: queries.map((query) => ({
      queryKey: ["public-show-inventory", showId, query],
      enabled: stage === "resolve" && !!showId,
      queryFn: ({ signal }: { signal: AbortSignal; }) =>
        fetchPublicShowInventory(showId!, query, signal),
      retry: 1,
      staleTime: 15_000,
    })),
  });
  const storedCards = huntList.hunt?.cards ?? [];
  const cardQueries = useQueries({
    queries: storedCards.map(({ cardId }) => ({
      queryKey: ["public-show-card", showId, cardId],
      enabled: stage === "route" && !!showId,
      queryFn: ({ signal }: { signal: AbortSignal; }) =>
        fetchPublicShowCard(showId!, cardId, signal),
      retry: 1,
      staleTime: 15_000,
    })),
  });

  const resolved = queries.flatMap((query, index) => {
    const cards = searches[index]?.data?.cards ?? [];
    const cardId = selected[query] ?? (cards.length === 1 ? cards[0]?.card_id : undefined);
    return cardId ? [{ query, cardId }] : [];
  }).filter((item, index, all) => all.findIndex((other) => other.cardId === item.cardId) === index);
  const ambiguous = queries.some((query, index) => {
    const cards = searches[index]?.data?.cards ?? [];
    return cards.length > 1 && !selected[query];
  });
  const searchesPending = searches.some((search) => search.isPending || search.isFetching);
  // A failed search would silently drop its card from the hunt, so name it and keep a per-card
  // retry, as the route stage does.
  const failedSearches = queries.filter((_, index) => searches[index]?.isError);
  // Only a successful listing or a "no longer listed" error counts as an answer. React Query keeps
  // stale `data` after a failed refetch, which would place a sold-out card at a table and save it
  // into the offline route.
  const cardReads = storedCards.map((card, index) => {
    const query = cardQueries[index];
    const delisted = query?.isError && isMissingShowListing(query.error);
    const detail = query?.isSuccess ? query.data ?? null : null;
    return {
      card,
      query,
      detail,
      delisted,
      answeredAt: delisted
        ? query?.errorUpdatedAt ?? 0
        : detail
        ? query?.dataUpdatedAt ?? 0
        : 0,
      awaiting: query?.isPending && query.fetchStatus !== "paused",
    };
  });
  const liveRoute = resolveShowHuntRoute(cardReads);
  const freshStops = liveRoute.stops;
  const refreshCompletedAt = liveRoute.refreshedAt;
  const savedRefresh = useRef(0);
  useEffect(() => {
    const snapshotSavedAt = savedRoute
      ? Date.parse(savedRoute.savedAt)
      : 0;
    if (
      !freshStops || !refreshCompletedAt || refreshCompletedAt <= snapshotSavedAt
      || savedRefresh.current === refreshCompletedAt
    ) return;
    savedRefresh.current = refreshCompletedAt;
    void saveRouteSnapshot(freshStops);
  }, [freshStops, refreshCompletedAt, saveRouteSnapshot, savedRoute]);

  const activeCardIds = new Set(storedCards.map((card) => card.cardId));
  const delistedCardIds = new Set(
    cardReads.flatMap((read) => read.delisted ? [read.card.cardId] : []),
  );
  const usingSavedRoute = freshStops === null && savedRoute !== null;
  const stops = (freshStops ?? savedRoute?.stops ?? []).flatMap((stop) => {
    const cards = stop.cards.filter((card) =>
      activeCardIds.has(card.card_id) && !delistedCardIds.has(card.card_id)
    );
    return cards.length ? [{ ...stop, cards }] : [];
  });
  const placedCardIds = new Set(stops.flatMap((stop) => stop.cards.map((card) => card.card_id)));
  // Only a route with no saved snapshot shows loading. Native queries pause while offline, which
  // counts as settled and unavailable, not as loading.
  const routeLoading = freshStops === null && (
    !snapshotHydrated
    || (!savedRoute && cardReads.some((read) => read.awaiting))
  );
  // Cards whose read failed are listed separately, so one unavailable card does not hide the rest
  // of the route.
  const unplaced = cardReads.flatMap(({ card, query, delisted }) => {
    if (!query) return [];
    const unavailable = query.isError || query.fetchStatus === "paused";
    if (!delisted && (!unavailable || placedCardIds.has(card.cardId))) return [];
    return [{ card, delisted, retry: query.refetch }];
  });
  const statuses = new Map(storedCards.map((card) => [card.cardId, card.status]));
  const found = storedCards.filter((card) => card.status === "found").length;
  const skipped = storedCards.filter((card) => card.status === "skipped").length;
  const remaining = storedCards.length - found - skipped;

  // The route stage runs from on-device storage: the Hunt List, Found and Skipped progress, and the
  // saved route snapshot. The live Show payload only supplies the header, and its offline copy is
  // not durable in every browser (WebKit drops the Cache Storage entry on reload), so an unreadable
  // Show must not blank a saved route.
  const savedRouteOnDevice = savedRoute !== null && storedCards.length > 0;

  const backToShow = () => router.dismissTo(`/shows/${encodeURIComponent(showId ?? "")}`);
  const edit = () => {
    setDraft(huntList.hunt?.inputLines.join("\n") ?? "");
    setQueries([]);
    setSelected({});
    setStage("edit");
  };

  if (showQuery.isLoading || (!showQuery.show && !snapshotHydrated)) {
    return <PublicShowSkeleton label="Loading Hunt List…" />;
  }

  if (!showId || (!showQuery.show && !savedRouteOnDevice)) {
    return (
      <Screen className="events-page items-center justify-center px-6" role="main">
        <Text className="text-xl font-title text-foreground">Hunt List unavailable</Text>
        <Button title="Back to Show" variant="outline" className="mt-4" onPress={backToShow} />
      </Screen>
    );
  }

  return (
    <Screen testID="show-hunt" className="events-page" role="main">
      <View
        className="w-full max-w-[980px] self-center flex-row items-center gap-2 border-b border-border px-2 pb-3"
        style={{ paddingTop: insets.top + 8 }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to Show"
          onPress={backToShow}
          className="h-11 w-11 items-center justify-center rounded-full active:bg-surface-muted"
        >
          <ChevronLeft size={22} color={colors.foreground} />
        </Pressable>
        <View className="min-w-0 flex-1">
          <Text className="font-title text-foreground" numberOfLines={1}>
            {showQuery.show?.name ?? "Hunt List"}
          </Text>
          <Text className="text-xs font-body text-muted-foreground">
            {stage === "route" ? "Your table route" : "Build a Hunt List"}
          </Text>
        </View>
      </View>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 40 }}
      >
        <View className="w-full max-w-[980px] self-center gap-5">
          {stage === "edit"
            ? (
              <View
                className={isDesktop
                  ? "flex-row items-start gap-8 rounded-xl border border-border bg-surface p-7"
                  : "gap-5 rounded-xl border border-border bg-surface p-5"}
              >
                <View className={isDesktop ? "w-80 shrink-0" : ""}>
                  <View className="h-11 w-11 items-center justify-center rounded-xl bg-accent-soft">
                    <ListChecks aria-hidden size={22} color={colors.primary} />
                  </View>
                  <Text
                    accessibilityRole="header"
                    aria-level={1}
                    className="mt-4 text-3xl font-title text-foreground"
                  >
                    What are you hunting for?
                  </Text>
                  <Text className="mt-2 font-body leading-6 text-muted-foreground">
                    Paste one card per line. Add the set or card number when you know it—we’ll ask
                    when a match is unclear.
                  </Text>
                </View>
                <View className="min-w-0 flex-1 gap-3">
                  <Input
                    testID="hunt-input"
                    value={draft}
                    onChangeText={setDraft}
                    placeholder={"Pikachu 58/102\nCharizard Base Set\nMew ex 151"}
                    accessibilityLabel="Cards to find, one per line"
                    multiline
                    textAlignVertical="top"
                    maxLength={1_200}
                    className="h-44 px-4 py-3"
                  />
                  <Text className="text-xs font-body text-muted-foreground">
                    Up to {MAX_HUNT_CARDS} unique cards · saved on this device
                  </Text>
                  <Button
                    title="Match my list"
                    disabled={!draft.trim()}
                    onPress={() => {
                      const next = parseHuntQueries(draft);
                      setQueries(next);
                      setSelected({});
                      setStage("resolve");
                    }}
                  />
                </View>
              </View>
            )
            : stage === "resolve"
            ? (
              <>
                <View className="gap-2">
                  <Badge
                    label={`STEP 2 · ${queries.length} ${queries.length === 1 ? "CARD" : "CARDS"}`}
                    className="bg-accent-soft"
                    textClassName="text-primary"
                  />
                  <Text
                    accessibilityRole="header"
                    aria-level={1}
                    className="text-3xl font-title text-foreground"
                  >
                    Confirm the cards
                  </Text>
                  <Text className="font-body text-muted-foreground">
                    Choose the right printing where more than one card matches.
                  </Text>
                </View>

                {queries.map((query, index) => {
                  const result = searches[index];
                  const cards = result?.data?.cards ?? [];
                  const chosen = selected[query]
                    ?? (cards.length === 1 ? cards[0]?.card_id : undefined);
                  return (
                    <View
                      key={query}
                      testID="hunt-query"
                      className="gap-2 rounded-xl border border-border bg-surface p-4"
                    >
                      <View className="flex-row items-center gap-2">
                        <Search aria-hidden size={16} color={colors.subtle} />
                        <Text className="min-w-0 flex-1 font-title text-foreground">{query}</Text>
                        {cards.length === 1
                          ? (
                            <Badge
                              label="MATCHED"
                              className="bg-gain/10"
                              textClassName="text-gain"
                            />
                          )
                          : null}
                      </View>
                      {result?.isPending || result?.isFetching
                        ? (
                          <View className="min-h-16 flex-row items-center gap-3">
                            <ActivityIndicator />
                            <Text className="font-body text-muted-foreground">
                              Checking Vendors…
                            </Text>
                          </View>
                        )
                        : result?.isError
                        ? (
                          <View className="gap-2">
                            <Text accessibilityRole="alert" className="font-body text-loss">
                              Search unavailable — this card won’t be added to the table route.
                            </Text>
                            <Button
                              title="Try again"
                              size="sm"
                              variant="outline"
                              className="self-start"
                              onPress={() => void result.refetch()}
                            />
                          </View>
                        )
                        : !cards.length
                        ? (
                          <View className="rounded-lg bg-surface-muted p-3">
                            <Text className="font-title text-foreground">
                              Not listed at this Show
                            </Text>
                            <Text className="mt-1 text-sm font-body text-muted-foreground">
                              This card won’t be added to the table route.
                            </Text>
                          </View>
                        )
                        : cards.map((card) => (
                          <Candidate
                            key={card.card_id}
                            card={card}
                            selected={chosen === card.card_id}
                            onPress={() =>
                              setSelected((current) => ({
                                ...current,
                                [query]: card.card_id,
                              }))}
                          />
                        ))}
                    </View>
                  );
                })}

                {failedSearches.length
                  ? (
                    <Text
                      testID="hunt-search-failed"
                      accessibilityRole="alert"
                      className="font-body text-loss"
                    >
                      {failedSearches.length === 1
                        ? "1 card could not be checked"
                        : `${failedSearches.length} cards could not be checked`}, so{" "}
                      {failedSearches.length === 1 ? "it stays" : "they stay"}{" "}
                      off the route. Try again above, or build the route without{" "}
                      {failedSearches.length === 1 ? "it" : "them"}.
                    </Text>
                  )
                  : null}

                <View className="flex-row flex-wrap justify-end gap-2 border-t border-border pt-4">
                  <Button title="Edit list" variant="outline" onPress={() => setStage("edit")} />
                  <Button
                    title={ambiguous ? "Choose each card" : "Build table route"}
                    loading={searchesPending}
                    disabled={ambiguous || !resolved.length}
                    onPress={() => {
                      huntList.save(queries, resolved);
                      setStage("route");
                    }}
                  />
                </View>
              </>
            )
            : (
              <>
                <View
                  className={isDesktop
                    ? "flex-row items-end justify-between gap-6"
                    : "gap-4"}
                >
                  <View className="min-w-0 flex-1 gap-2">
                    <Badge
                      label={`${remaining} LEFT · ${found} FOUND · ${skipped} SKIPPED`}
                      className="bg-accent-soft"
                      textClassName="text-primary"
                    />
                    <Text
                      accessibilityRole="header"
                      aria-level={1}
                      className="text-3xl font-title text-foreground"
                    >
                      Your table route
                    </Text>
                    <Text className="font-body text-muted-foreground">
                      {stops.length}{" "}
                      {stops.length === 1 ? "stop" : "stops"}, ordered by table. Inventory can
                      change during the Show.
                    </Text>
                    {savedRoute
                      ? (
                        <Text
                          testID="hunt-snapshot-status"
                          accessibilityLiveRegion="polite"
                          className={usingSavedRoute
                            ? "text-sm font-body text-warning"
                            : "text-xs font-body text-muted-foreground"}
                        >
                          {usingSavedRoute
                            ? "Using saved route · Updated "
                            : "Saved on this device · Updated "}
                          <Text className="font-num">
                            {savedAtFormatter.format(new Date(savedRoute.savedAt))}
                          </Text>
                          {usingSavedRoute ? ". Inventory and tables may have changed." : "."}
                        </Text>
                      )
                      : null}
                  </View>
                  <Button title="Edit wanted cards" variant="outline" onPress={edit} />
                </View>

                {routeLoading
                  ? (
                    <View className="min-h-52 items-center justify-center rounded-xl border border-border bg-surface">
                      <ActivityIndicator />
                      <Text className="mt-3 font-body text-muted-foreground">
                        Building table route…
                      </Text>
                    </View>
                  )
                  : stops.map((stop, stopIndex) => {
                    const stopDone = stop.cards.every((card) =>
                      statuses.get(card.card_id) !== "wanted"
                    );
                    return (
                      <View
                        key={stop.vendor.participation_id}
                        testID="hunt-stop"
                        // A finished stop is marked by its border and "STOP DONE" badge, not reduced
                        // opacity. Found and Skip stay pressable, so WCAG's inactive-control exception
                        // does not apply, and dimming drops text below AA contrast. A muted background
                        // also fails, because surfaces are translucent and it
                        // darkens the header's muted fill; a border affects no text contrast.
                        className={stopDone
                          ? "overflow-hidden rounded-xl border border-gain bg-surface"
                          : "overflow-hidden rounded-xl border border-border bg-surface"}
                      >
                        <View className="flex-row items-center gap-4 border-b border-border bg-surface-muted p-4">
                          <View className="h-10 w-10 items-center justify-center rounded-full bg-primary">
                            <Text className="font-num-bold text-primary-foreground">
                              {stopIndex + 1}
                            </Text>
                          </View>
                          <View className="min-w-0 flex-1">
                            <Text className="text-xs font-label uppercase tracking-wider text-primary">
                              {tableLabel(stop.vendor.table_numbers)}
                            </Text>
                            <Text className="text-xl font-title text-foreground">
                              {stop.vendor.name}
                            </Text>
                          </View>
                          {stopDone
                            ? (
                              <Badge
                                label="STOP DONE"
                                className="bg-gain/10"
                                textClassName="text-gain"
                              />
                            )
                            : null}
                        </View>
                        <View className="divide-y divide-border">
                          {stop.cards.map((card) => {
                            const status = statuses.get(card.card_id) ?? "wanted";
                            const identity = formatCardIdentity(
                              card.set_name,
                              card.collector_number,
                              treatmentLabel(card),
                            );
                            return (
                              <View key={card.card_id} testID="hunt-card" className="gap-3 p-4">
                                <View className="flex-row items-center gap-3">
                                  {card.image_url
                                    ? (
                                      <Image
                                        source={{ uri: card.image_url }}
                                        className="h-16 w-11 rounded-md bg-surface-muted"
                                        resizeMode="cover"
                                        accessible={false}
                                        accessibilityIgnoresInvertColors
                                      />
                                    )
                                    : null}
                                  <View className="min-w-0 flex-1 gap-1">
                                    <Text className="font-title text-foreground">{card.name}</Text>
                                    {identity
                                      ? (
                                        <Text className="text-xs font-body text-muted-foreground">
                                          {identity}
                                        </Text>
                                      )
                                      : null}
                                    {card.vendor_count > 1
                                      ? (
                                        <Text className="text-xs font-body text-muted-foreground">
                                          Also listed by {card.vendor_count - 1} other
                                          {card.vendor_count === 2
                                            ? " Vendor"
                                            : " Vendors"}
                                        </Text>
                                      )
                                      : null}
                                  </View>
                                  <Badge
                                    label={statusLabel(status).toUpperCase()}
                                    className={status === "found"
                                      ? "bg-gain/10"
                                      : status === "skipped"
                                      ? "bg-surface-muted"
                                      : "bg-warning-soft"}
                                    textClassName={status === "found"
                                      ? "text-gain"
                                      : status === "skipped"
                                      ? "text-muted-foreground"
                                      : "text-warning"}
                                  />
                                </View>
                                <View className="flex-row gap-2 self-end">
                                  <Button
                                    title="Found"
                                    size="sm"
                                    variant={status === "found" ? "default" : "outline"}
                                    aria-pressed={status === "found"}
                                    onPress={() =>
                                      huntList.setStatus(
                                        card.card_id,
                                        status === "found" ? "wanted" : "found",
                                      )}
                                  />
                                  <Button
                                    title="Skip"
                                    size="sm"
                                    variant="ghost"
                                    aria-pressed={status === "skipped"}
                                    onPress={() =>
                                      huntList.setStatus(
                                        card.card_id,
                                        status === "skipped" ? "wanted" : "skipped",
                                      )}
                                  >
                                    <View className="flex-row items-center gap-1">
                                      <SkipForward
                                        aria-hidden
                                        size={15}
                                        color={colors.foreground}
                                      />
                                      <Text className="font-title text-foreground">Skip</Text>
                                    </View>
                                  </Button>
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      </View>
                    );
                  })}

                {routeLoading || !unplaced.length ? null : (
                  <View
                    testID="hunt-unplaced"
                    className="gap-3 rounded-xl border border-border bg-surface p-4"
                  >
                    <Text accessibilityRole="alert" className="font-title text-foreground">
                      {unplaced.length} {unplaced.length === 1 ? "card is" : "cards are"}{" "}
                      not on the route
                    </Text>
                    {unplaced.map(({ card, delisted, retry }) => (
                      <View
                        key={card.cardId}
                        testID="hunt-unplaced-card"
                        className="gap-2 border-t border-border pt-3"
                      >
                        <Text className="font-body text-foreground">
                          {card.query ?? "A card on your list"}
                        </Text>
                        <Text className="text-sm font-body text-muted-foreground">
                          {delisted
                            ? "No longer listed at this Show — the last copy sold."
                            : "We could not reach this card's Vendors just now."}
                        </Text>
                        {delisted
                          ? (
                            <Button
                              title={statuses.get(card.cardId) === "skipped"
                                ? "Skipped"
                                : "Skip this card"}
                              size="sm"
                              variant={statuses.get(card.cardId) === "skipped"
                                ? "default"
                                : "outline"}
                              aria-pressed={statuses.get(card.cardId) === "skipped"}
                              className="self-start"
                              onPress={() =>
                                huntList.setStatus(
                                  card.cardId,
                                  statuses.get(card.cardId) === "skipped" ? "wanted" : "skipped",
                                )}
                            />
                          )
                          : (
                            <Button
                              title="Try again"
                              size="sm"
                              variant="outline"
                              className="self-start"
                              onPress={() => void retry()}
                            />
                          )}
                      </View>
                    ))}
                  </View>
                )}

                {routeLoading || stops.length || unplaced.length
                  ? null
                  : (
                    <View className="rounded-xl border border-border bg-surface p-5">
                      <Text className="font-title text-foreground">Nothing to walk to yet</Text>
                      <Text className="mt-1 font-body text-muted-foreground">
                        None of your cards are listed at this Show right now.
                      </Text>
                    </View>
                  )}
              </>
            )}
        </View>
      </ScrollView>
    </Screen>
  );
}
